import { useEffect, useRef, useState } from 'react';
import { Star, Flag, CheckCircle, ThumbsUp } from '../../components/icons';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { SkeletonRows } from '../../components/skeleton';
import { Pagination } from '../../components/pagination';
import { extractErr } from '../../api/error-message';
import { PageHeader } from '../../components/page-header';
import { EmptyState } from '../../components/empty-state';
import { TableSearch, useDebouncedValue } from '../../components/table-search';
import { useConfirm } from '../../components/confirm-dialog';

// Wired to GET /admin/reviews + remove/restore. Reviews auto-publish on submit
// (verified purchase), so moderation here is remove/restore rather than a
// pending-approval queue. A "Low ratings" view surfaces 1–2★ for triage.
//
// The list is paged by the API, so search goes to the server as `q` and matches
// every field the card shows: product, seller, review title/body, customer,
// star rating ("1★") and date (dd/mm/yyyy).

const PER_PAGE = 15;

interface ReviewRow {
  id: string;
  rating: number;
  title: string | null;
  body: string | null;
  imageUrls: string[];
  isPublished: boolean;
  createdAt: string;
  customer: { fullName: string } | null;
  product: { title: string; seller: { businessName: string } | null } | null;
}

type Tab = 'published' | 'low' | 'removed';

function Stars({ rating, size = 14 }: { rating: number; size?: number }) {
  return (
    <span className="flex">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star key={s} size={size} className={s <= rating ? 'fill-warning text-warning' : 'text-ink-200'} />
      ))}
    </span>
  );
}

export function ReviewsPage() {
  const [tab, setTab] = useState<Tab>('published');
  const [rows, setRows] = useState<ReviewRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const q = useDebouncedValue(search.trim(), 300);
  const { confirm } = useConfirm();

  // Monotonic request id — only the latest load's response is applied, so a
  // fast tab switch (which fires a stale-page load then a page=1 load) can't
  // render out-of-order results.
  const seq = useRef(0);
  const load = async () => {
    const my = ++seq.current;
    setLoading(true);
    try {
      const params: Record<string, unknown> = { page, limit: PER_PAGE };
      if (tab === 'removed') params.published = 'false';
      else params.published = 'true';
      if (tab === 'low') params.minRating = 2; // service filters rating <= 2
      if (q) params.q = q;
      const { data } = await api.get<{ items: ReviewRow[]; total: number }>('/admin/reviews', { params });
      if (my !== seq.current) return; // superseded by a newer load
      setRows(data.items);
      setTotal(data.total);
    } catch (err) {
      if (my === seq.current) toast.error(extractErr(err));
    } finally {
      if (my === seq.current) setLoading(false);
    }
  };

  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [tab, page, q]);
  useEffect(() => { setPage(1); }, [tab, q]);

  const act = async (r: ReviewRow, action: 'remove' | 'restore') => {
    if (action === 'remove') {
      const ok = await confirm({
        title: 'Remove this review?',
        description: `It disappears from ${r.product?.title ?? 'the product'} and stops counting toward its rating. You can restore it from the Removed tab.`,
        confirmLabel: 'Remove review',
        tone: 'danger',
      });
      if (!ok) return;
    }
    setBusyId(r.id);
    try {
      await api.post(`/admin/reviews/${r.id}/${action}`);
      toast.success(action === 'remove' ? 'Review removed' : 'Review restored');
      await load();
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setBusyId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  const TABS: { key: Tab; label: string }[] = [
    { key: 'published', label: 'Published' },
    { key: 'low', label: 'Low ratings (≤2★)' },
    { key: 'removed', label: 'Removed' },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Reviews" description="Moderate product ratings and content." />

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            aria-pressed={tab === t.key}
            onClick={() => setTab(t.key)}
            className="fchip"
          >
            {t.label}
          </button>
        ))}
      </div>

      <TableSearch
        value={search}
        onChange={setSearch}
        placeholder="Search by product, seller, customer, review text, rating or date"
        resultCount={!loading && q ? total : undefined}
      />

      {loading ? (
        <div className="overflow-hidden panel"><SkeletonRows cols={4} /></div>
      ) : rows.length === 0 && q ? (
        <EmptyState
          icon="search_off"
          title={`No matches for "${q}"`}
          body="Try a product, seller or customer name, a word from the review, a star rating like 1★, or a date."
          action={
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setSearch('')}>
              Clear search
            </button>
          }
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon="reviews"
          title="Nothing here"
          body={
            tab === 'removed'
              ? 'Reviews you remove land here and can be restored.'
              : tab === 'low'
                ? 'No 1–2★ reviews to triage right now.'
                : 'Reviews appear here once customers rate a delivered order.'
          }
        />
      ) : (
        <div className="space-y-4">
          {rows.map((review) => (
            <div key={review.id} className="panel p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="mb-2 flex items-center gap-3">
                    <Stars rating={review.rating} />
                    <div>
                      <span className="text-[14px] font-semibold text-ink-900">{review.product?.title ?? 'Product'}</span>
                      <span className="ml-2 text-[12.5px] text-ink-500">by {review.product?.seller?.businessName ?? 'Seller'}</span>
                    </div>
                  </div>
                  {review.title && <p className="text-[15px] font-semibold text-ink-900">{review.title}</p>}
                  {review.body && <p className="mt-0.5 text-[14.5px] leading-relaxed text-ink-700">{review.body}</p>}
                  <div className="mt-3 flex flex-wrap items-center gap-4 text-[12.5px] text-ink-500">
                    <span>By <span className="font-medium text-ink-700">{review.customer?.fullName ?? 'Customer'}</span></span>
                    <span>{new Date(review.createdAt).toLocaleDateString('en-IN')}</span>
                    {Array.isArray(review.imageUrls) && review.imageUrls.length > 0 && (
                      <span className="flex items-center gap-1 font-medium text-admin-ink"><ThumbsUp size={11} /> {review.imageUrls.length} photo{review.imageUrls.length > 1 ? 's' : ''}</span>
                    )}
                    {!review.isPublished && <span className="badge badge-danger">Removed</span>}
                  </div>
                </div>
                <div className="flex shrink-0 flex-col gap-2">
                  {review.isPublished ? (
                    <button type="button" disabled={busyId === review.id} onClick={() => void act(review, 'remove')}
                      className="btn btn-danger-outline btn-sm">
                      <Flag size={14} /> Remove
                    </button>
                  ) : (
                    <button type="button" disabled={busyId === review.id} onClick={() => void act(review, 'restore')}
                      className="btn btn-secondary btn-sm">
                      <CheckCircle size={14} /> Restore
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && <Pagination page={page} totalPages={totalPages} totalItems={total} perPage={PER_PAGE} onPageChange={setPage} />}
    </div>
  );
}
